type InferType<P> = P extends Type<infer R> ? R : never;

export type Type<T> = {
  matches(value: unknown): value is T;
  nullable(): Type<T | null>;
  oneOf<U extends T>(variants: U[]): Type<U>;
};

function makeType<T>(matcher: (value: unknown) => boolean): Type<T> {
  return {
    matches(value: unknown): value is T {
      return matcher(value);
    },

    nullable(): Type<T | null> {
      return makeType<T | null>((v) => v === null || matcher(v));
    },

    oneOf<U extends T>(variants: U[]): Type<U> {
      return makeType<U>((v) => matcher(v) && variants.includes(v as U));
    },
  };
}

export const Types = {
  is<T>(value: T): Type<T> {
    return makeType<T>((v) => v === value);
  },

  matches<T>(fn: (value: unknown) => boolean): Type<T> {
    return makeType<T>(fn);
  },

  anyOf<T>(types: Type<T>[]): Type<T> {
    return makeType<T>((v) => types.some((t) => t.matches(v)));
  },

  string: (() => {
    const type = makeType<string>((v) => typeof v === 'string');

    return () => type;
  })(),

  number: (() => {
    const type = makeType<number>(
      (v) => typeof v === 'number' && Number.isFinite(v),
    );

    return () => type;
  })(),

  integer: (() => {
    const type = makeType<number>(
      (v) => typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v),
    );

    return () => type;
  })(),

  boolean: (() => {
    const type = makeType<boolean>((v) => typeof v === 'boolean');

    return () => type;
  })(),

  enum<T extends Record<string, unknown>>(variants: T): Type<T[keyof T]> {
    const values = Object.values(variants) as T[keyof T][];

    return makeType<T[keyof T]>((v) => values.includes(v as T[keyof T]));
  },

  array<T>(type: Type<T>): Type<T[]> {
    return makeType<T[]>(
      (v) => Array.isArray(v) && v.every((item) => type.matches(item)),
    );
  },

  // eslint-disable-next-line
  object<T extends Record<string, Type<any>>>(
    shape: T,
    options?: { strict?: boolean },
  ): Type<{ [K in keyof T]: InferType<T[K]> }> {
    return makeType((v) => {
      if (typeof v !== 'object' || v === null || Array.isArray(v)) {
        return false;
      }

      for (const [key, value] of Object.entries(shape)) {
        if (!value.matches((v as Record<string, unknown>)[key])) {
          return false;
        }
      }

      if (options?.strict) {
        for (const key of Object.keys(v as Record<string, unknown>)) {
          if (!(key in shape)) {
            return false;
          }
        }
      }

      return true;
    });
  },
};
